import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';

export function Footer({ onContactClick }) {
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  // Handle smooth scroll to section
  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    
    // If we're not on the homepage, navigate there first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // We're on homepage, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const handleContactClick = (e) => {
    e.preventDefault();
    if (onContactClick) {
      onContactClick();
    }
  };

  return (
    <footer className="bg-white border-t border-pp-border" data-testid="footer">
      <div className="pp-container py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          {/* Brand */}
          <div className="md:col-span-5">
            <Link to="/" className="flex items-center gap-2.5 mb-5">
              <div className="w-9 h-9 bg-pp-navy rounded-xl flex items-center justify-center">
                <span className="text-white font-heading font-bold text-lg">P</span>
              </div>
              <span className="font-heading font-semibold text-xl text-pp-text">PaperPal</span>
            </Link>
            <p className="text-pp-text-secondary leading-relaxed mb-6 max-w-sm">
              {t('footer.tagline')}
            </p>
            
            {/* Language Toggle */}
            <div className="flex items-center bg-pp-secondary rounded-full p-1 w-fit">
              <button
                onClick={() => language !== 'en' && toggleLanguage()}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  language === 'en' 
                    ? 'bg-white text-pp-navy shadow-sm' 
                    : 'text-pp-text-muted hover:text-pp-text-secondary'
                }`}
              >
                English
              </button>
              <button
                onClick={() => language !== 'fr' && toggleLanguage()}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  language === 'fr' 
                    ? 'bg-white text-pp-navy shadow-sm' 
                    : 'text-pp-text-muted hover:text-pp-text-secondary'
                }`}
                data-testid="footer-language-toggle"
              >
                Français
              </button>
            </div>
          </div>

          {/* Links - Product */}
          <div className="md:col-span-2">
            <h4 className="pp-overline text-pp-text mb-5" data-testid="footer-product-heading">{language === 'fr' ? 'Produit' : 'Product'}</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="/#how-it-works"
                  onClick={(e) => scrollToSection(e, 'how-it-works')}
                  className="text-pp-text-secondary hover:text-pp-text transition-colors cursor-pointer"
                >
                  {t('nav.howItWorks')}
                </a>
              </li>
              <li>
                <a 
                  href="/#pricing"
                  onClick={(e) => scrollToSection(e, 'pricing')}
                  className="text-pp-text-secondary hover:text-pp-text transition-colors cursor-pointer"
                >
                  {t('nav.pricing')}
                </a>
              </li>
              <li>
                <a 
                  href="/#faq"
                  onClick={(e) => scrollToSection(e, 'faq')}
                  className="text-pp-text-secondary hover:text-pp-text transition-colors cursor-pointer"
                >
                  {t('nav.faq')}
                </a>
              </li>
            </ul>
          </div>

          {/* Links - Company */}
          <div className="md:col-span-2">
            <h4 className="pp-overline text-pp-text mb-5">{language === 'fr' ? 'Entreprise' : 'Company'}</h4>
            <ul className="space-y-4">
              <li>
                <button 
                  onClick={handleContactClick}
                  className="text-pp-text-secondary hover:text-pp-text transition-colors cursor-pointer text-left"
                  data-testid="footer-contact-link"
                >
                  {t('footer.contact')}
                </button>
              </li>
            </ul>
          </div>

          {/* Links - Legal */}
          <div className="md:col-span-3">
            <h4 className="pp-overline text-pp-text mb-5">{language === 'fr' ? 'Légal' : 'Legal'}</h4>
            <ul className="space-y-4">
              <li>
                <Link 
                  to="/disclaimer" 
                  className="text-pp-text-secondary hover:text-pp-text transition-colors"
                >
                  {t('footer.disclaimer')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/privacy" 
                  className="text-pp-text-secondary hover:text-pp-text transition-colors"
                >
                  {t('footer.privacy')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/terms" 
                  className="text-pp-text-secondary hover:text-pp-text transition-colors"
                >
                  {t('footer.terms')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom - Copyright */}
        <div className="mt-16 pt-8 border-t border-pp-border">
          <div className="flex items-center justify-center">
            <span className="text-sm text-pp-text-muted">
              © 2026 PaperPal. {language === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
